import type Anthropic from '@anthropic-ai/sdk';
import { nanoid } from 'nanoid';
import { useAppStore } from '../stores/useAppStore';
import { prose as proseStore, outlines as outlineStore } from '../storage/db';
import { getTemplate, getDefault } from '../templates/registry';
import { TEMPLATE_DESCRIPTIONS, TEMPLATE_ORDER } from '../templates/registry';
import type { PromptTemplateId, Slug, Vibe } from '../types';
import { VIBE_ORDER } from '../types';

export type ToolKind = 'read' | 'write';
export type ToolDispatchContext = { universeId: string };
export type ToolOutcome = { content: string; is_error?: boolean };

type ToolDef = {
  name: string;
  description: string;
  kind: ToolKind;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
};

const TEMPLATE_ID_ENUM: PromptTemplateId[] = [...TEMPLATE_ORDER];

const TOOL_DEFS: ToolDef[] = [
  {
    name: 'list_stories',
    kind: 'read',
    description:
      'List stories in the current universe with id, title, hook, stage, and updatedAt. Optionally filter by stage.',
    input_schema: {
      type: 'object',
      properties: {
        stage: {
          type: 'string',
          enum: ['slug', 'outline', 'prose', 'tts', 'ready', 'archived'],
          description: 'Optional stage filter.',
        },
      },
    },
  },
  {
    name: 'read_story',
    kind: 'read',
    description:
      "Read parts of a specific story. parts can include 'outline' and/or 'prose'. Returns whatever is available; missing parts are omitted.",
    input_schema: {
      type: 'object',
      properties: {
        storyId: { type: 'string' },
        parts: {
          type: 'array',
          items: { type: 'string', enum: ['outline', 'prose'] },
          description: 'Which parts of the story to fetch.',
        },
      },
      required: ['storyId', 'parts'],
    },
  },
  {
    name: 'read_canon',
    kind: 'read',
    description:
      'Read the current canon text for the universe (auto-extracted facts gardened across stories).',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'read_prompt',
    kind: 'read',
    description:
      'Read a prompt template: returns its shipped default plus the universe override and extension if present.',
    input_schema: {
      type: 'object',
      properties: {
        templateId: { type: 'string', enum: TEMPLATE_ID_ENUM },
      },
      required: ['templateId'],
    },
  },
  {
    name: 'list_prompt_templates',
    kind: 'read',
    description:
      'List all prompt template ids and a one-line description of each. Use before read_prompt to see what is available.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'read_universe_meta',
    kind: 'read',
    description:
      'Read the universe identity: name, description, default protagonists.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'propose_slugs',
    kind: 'read',
    description:
      'Offer the user a batch of story slugs as tappable cards in this chat. Each slug is a short story idea with a title and a one-sentence hook. Use this when the user discusses a plot concept, theme, or multi-story arc and wants concrete options to choose from. The proposed slugs are added to this universe\'s slug pool with source="chat" — the user can then auto-build or open any of them. Pass a "note" describing the framing/arc; it becomes the batch guidance threaded into outline+prose generation.',
    input_schema: {
      type: 'object',
      properties: {
        slugs: {
          type: 'array',
          minItems: 1,
          maxItems: 12,
          items: {
            type: 'object',
            properties: {
              title: { type: 'string', description: 'Short, evocative story title.' },
              hook: { type: 'string', description: 'One-sentence premise / what happens.' },
              vibe: {
                type: 'string',
                enum: VIBE_ORDER,
                description: 'Optional mood for this slug.',
              },
            },
            required: ['title', 'hook'],
          },
        },
        note: {
          type: 'string',
          description:
            'Brief framing for this batch (motivation, arc, constraints). Threads into outline+prose as batch guidance.',
        },
      },
      required: ['slugs'],
    },
  },

  {
    name: 'update_canon',
    kind: 'write',
    description:
      "Replace the universe's canon text with newText. Use for substantive edits; preserve the existing structure (## headers) unless reorganizing intentionally.",
    input_schema: {
      type: 'object',
      properties: { newText: { type: 'string' } },
      required: ['newText'],
    },
  },
  {
    name: 'set_prompt_override',
    kind: 'write',
    description:
      'Replace a prompt template entirely with a custom version. The shipped default will no longer apply for this universe. Use sparingly — for most tweaks, prefer set_prompt_extension.',
    input_schema: {
      type: 'object',
      properties: {
        templateId: { type: 'string', enum: TEMPLATE_ID_ENUM },
        newText: { type: 'string' },
      },
      required: ['templateId', 'newText'],
    },
  },
  {
    name: 'clear_prompt_override',
    kind: 'write',
    description:
      'Remove a prompt override so the shipped default applies again.',
    input_schema: {
      type: 'object',
      properties: {
        templateId: { type: 'string', enum: TEMPLATE_ID_ENUM },
      },
      required: ['templateId'],
    },
  },
  {
    name: 'set_prompt_extension',
    kind: 'write',
    description:
      'Set the extension text for a prompt template. The extension is appended after the main template (default or override) and survives default updates. Preferred over override for small additions.',
    input_schema: {
      type: 'object',
      properties: {
        templateId: { type: 'string', enum: TEMPLATE_ID_ENUM },
        newText: { type: 'string' },
      },
      required: ['templateId', 'newText'],
    },
  },
  {
    name: 'clear_prompt_extension',
    kind: 'write',
    description: 'Remove the extension for a prompt template.',
    input_schema: {
      type: 'object',
      properties: {
        templateId: { type: 'string', enum: TEMPLATE_ID_ENUM },
      },
      required: ['templateId'],
    },
  },
  {
    name: 'update_universe_meta',
    kind: 'write',
    description:
      "Update universe identity fields. Provide only the fields you want to change.",
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        description: { type: 'string' },
        defaultProtagonists: { type: 'string' },
      },
    },
  },
];

const TOOL_BY_NAME = new Map<string, ToolDef>(TOOL_DEFS.map((t) => [t.name, t]));

export const TOOLS_FOR_API: Anthropic.Tool[] = TOOL_DEFS.map((t) => ({
  name: t.name,
  description: t.description,
  input_schema: t.input_schema as Anthropic.Tool.InputSchema,
}));

export function getToolKind(name: string): ToolKind | undefined {
  return TOOL_BY_NAME.get(name)?.kind;
}

export function getToolDescription(name: string): string | undefined {
  return TOOL_BY_NAME.get(name)?.description;
}

function isPromptTemplateId(id: unknown): id is PromptTemplateId {
  return typeof id === 'string' && TEMPLATE_ID_ENUM.includes(id as PromptTemplateId);
}

function ok(content: unknown): ToolOutcome {
  return { content: typeof content === 'string' ? content : JSON.stringify(content) };
}

function err(message: string): ToolOutcome {
  return { content: message, is_error: true };
}

export async function executeRead(
  name: string,
  input: unknown,
  ctx: ToolDispatchContext
): Promise<ToolOutcome> {
  const args = (input ?? {}) as Record<string, unknown>;
  const state = useAppStore.getState();
  const universe = state.universes[ctx.universeId];
  if (!universe) return err('Universe not found.');

  switch (name) {
    case 'list_stories': {
      const stage = typeof args.stage === 'string' ? args.stage : undefined;
      const items = Object.values(state.stories)
        .filter((s) => s.universeId === ctx.universeId && !s.hidden)
        .filter((s) => (stage ? s.stage === stage : true))
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((s) => ({
          id: s.id,
          title: s.slug.title,
          hook: s.slug.hook,
          stage: s.stage,
          updatedAt: s.updatedAt,
        }));
      return ok({ count: items.length, stories: items });
    }
    case 'read_story': {
      const storyId = typeof args.storyId === 'string' ? args.storyId : '';
      const parts = Array.isArray(args.parts) ? (args.parts as string[]) : [];
      const story = state.stories[storyId];
      if (!story || story.universeId !== ctx.universeId)
        return err('Story not found in this universe.');
      const result: Record<string, unknown> = {
        id: story.id,
        title: story.slug.title,
        stage: story.stage,
      };
      if (parts.includes('outline')) {
        result.outline = story.outlineText
          ?? (story.outline ? story.outline.map((b) => `## ${b.heading}\n\n${b.body}`).join('\n\n') : undefined)
          ?? (story.id ? await outlineStore.get(story.id).catch(() => undefined) : undefined)
          ?? null;
      }
      if (parts.includes('prose')) {
        result.prose = story.proseKey
          ? (await proseStore.get(story.proseKey).catch(() => undefined)) ?? null
          : null;
      }
      return ok(result);
    }
    case 'read_canon': {
      return ok(universe.canon?.trim() || '(empty — no canon yet)');
    }
    case 'read_prompt': {
      const templateId = args.templateId;
      if (!isPromptTemplateId(templateId)) return err('Unknown templateId.');
      return ok({
        templateId,
        default: getDefault(templateId),
        override: universe.overrides[templateId] ?? null,
        extension: universe.extensions[templateId] ?? null,
        effective: getTemplate(templateId, ctx.universeId),
      });
    }
    case 'list_prompt_templates': {
      return ok(
        TEMPLATE_ORDER.map((id) => ({
          templateId: id,
          description: TEMPLATE_DESCRIPTIONS[id],
          overridden: universe.overrides[id] != null,
          extended: !!universe.extensions[id]?.trim(),
        }))
      );
    }
    case 'read_universe_meta': {
      return ok({
        name: universe.name,
        description: universe.description ?? null,
        defaultProtagonists: universe.defaultProtagonists,
      });
    }
    case 'propose_slugs': {
      const rawSlugs = Array.isArray(args.slugs) ? args.slugs : null;
      if (!rawSlugs || rawSlugs.length === 0)
        return err('slugs must be a non-empty array.');
      const note = typeof args.note === 'string' ? args.note.trim() : '';
      const guidance = note || undefined;
      const batchId = nanoid(8);
      const now = Date.now();
      const made: Slug[] = [];
      for (const raw of rawSlugs) {
        const r = (raw ?? {}) as Record<string, unknown>;
        const title = typeof r.title === 'string' ? r.title.trim() : '';
        const hook = typeof r.hook === 'string' ? r.hook.trim() : '';
        if (!title || !hook) continue;
        const vibe =
          typeof r.vibe === 'string' && (VIBE_ORDER as string[]).includes(r.vibe)
            ? (r.vibe as Vibe)
            : undefined;
        made.push({
          id: nanoid(10),
          universeId: ctx.universeId,
          title,
          hook,
          createdAt: now,
          batchId,
          vibe,
          guidance,
          source: 'chat',
        });
      }
      if (made.length === 0)
        return err('No valid slugs (each needs both title and hook).');
      state.addSlugs(made);
      return ok({
        batchId,
        note: note || null,
        slugIds: made.map((s) => s.id),
        slugs: made.map((s) => ({
          id: s.id,
          title: s.title,
          hook: s.hook,
          vibe: s.vibe ?? null,
        })),
      });
    }
    default:
      return err(`Unknown read tool: ${name}`);
  }
}

export async function executeWrite(
  name: string,
  input: unknown,
  ctx: ToolDispatchContext
): Promise<ToolOutcome> {
  const args = (input ?? {}) as Record<string, unknown>;
  const store = useAppStore.getState();
  const universe = store.universes[ctx.universeId];
  if (!universe) return err('Universe not found.');

  switch (name) {
    case 'update_canon': {
      const newText = typeof args.newText === 'string' ? args.newText : null;
      if (newText == null) return err('newText is required.');
      store.setUniverseCanon(ctx.universeId, newText);
      return ok(
        'Canon replaced with your new text. It is now the live canon and will be injected into every future story stage.'
      );
    }
    case 'set_prompt_override': {
      const templateId = args.templateId;
      const newText = typeof args.newText === 'string' ? args.newText : null;
      if (!isPromptTemplateId(templateId)) return err('Unknown templateId.');
      if (newText == null) return err('newText is required.');
      store.setUniverseOverride(ctx.universeId, templateId, newText);
      return ok(
        `Override applied to ${templateId}. The shipped default no longer applies for this universe; your override is the new base, and any existing extension is still appended after it.`
      );
    }
    case 'clear_prompt_override': {
      const templateId = args.templateId;
      if (!isPromptTemplateId(templateId)) return err('Unknown templateId.');
      store.clearUniverseOverride(ctx.universeId, templateId);
      return ok(
        `Override cleared for ${templateId}. The shipped default applies again, with any existing extension appended after it.`
      );
    }
    case 'set_prompt_extension': {
      const templateId = args.templateId;
      const newText = typeof args.newText === 'string' ? args.newText : null;
      if (!isPromptTemplateId(templateId)) return err('Unknown templateId.');
      if (newText == null) return err('newText is required.');
      store.setUniverseExtension(ctx.universeId, templateId, newText);
      return ok(
        `Extension applied to ${templateId}. It is now appended (after a blank line) to the main template — your text below is the active extension. Call read_prompt to see the combined effective template if you want to verify.`
      );
    }
    case 'clear_prompt_extension': {
      const templateId = args.templateId;
      if (!isPromptTemplateId(templateId)) return err('Unknown templateId.');
      store.clearUniverseExtension(ctx.universeId, templateId);
      return ok(
        `Extension cleared for ${templateId}. The main template (default or override) is now used without an appended extension.`
      );
    }
    case 'update_universe_meta': {
      const patch: Record<string, unknown> = {};
      if (typeof args.name === 'string') patch.name = args.name;
      if (typeof args.description === 'string') patch.description = args.description;
      if (typeof args.defaultProtagonists === 'string')
        patch.defaultProtagonists = args.defaultProtagonists;
      if (Object.keys(patch).length === 0) return err('No fields to update.');
      store.updateUniverse(ctx.universeId, patch);
      return ok(`Universe meta updated: ${Object.keys(patch).join(', ')}.`);
    }
    default:
      return err(`Unknown write tool: ${name}`);
  }
}

export function summarizeToolUse(name: string, input: unknown): string {
  const args = (input ?? {}) as Record<string, unknown>;
  switch (name) {
    case 'list_stories':
      return args.stage ? `list stories (${args.stage})` : 'list stories';
    case 'read_story':
      return `read story ${String(args.storyId).slice(0, 6)} (${(args.parts as string[] | undefined)?.join(', ') ?? ''})`;
    case 'read_canon':
      return 'read canon';
    case 'read_prompt':
      return `read prompt: ${args.templateId}`;
    case 'list_prompt_templates':
      return 'list prompt templates';
    case 'read_universe_meta':
      return 'read universe meta';
    case 'propose_slugs': {
      const n = Array.isArray(args.slugs) ? args.slugs.length : 0;
      return `propose ${n} slug${n === 1 ? '' : 's'}`;
    }
    case 'update_canon':
      return 'update canon';
    case 'set_prompt_override':
      return `override prompt: ${args.templateId}`;
    case 'clear_prompt_override':
      return `clear override: ${args.templateId}`;
    case 'set_prompt_extension':
      return `extend prompt: ${args.templateId}`;
    case 'clear_prompt_extension':
      return `clear extension: ${args.templateId}`;
    case 'update_universe_meta':
      return 'update universe meta';
    default:
      return name;
  }
}
